import Image from "next/image";

import { isProd } from "@/Env";
import { cn } from "@/libs/utils";
import ViewportVideo from "@/v2-components/pages/games/viewport-video";

import type { CoolingFanFigureSlot } from "./cooling-fan-assets";

/**
 * 쿨링 팬 페이지의 이미지 한 자리.
 *
 * 시안의 이미지 자리는 2026-10-01 기준 **12곳 전부 채움**(STEP 03 은 영상)이다
 * (`cooling-fan-assets.ts` 실측표). 에셋이 도착하기 전까지 이 컴포넌트가 그 자리를 그린다.
 * 세 가지 분기를 가진다 — **아트 디렉션 2컷(`<picture>`) · 단일 에셋(`next/image`) ·
 * 플레이스홀더**.
 *
 * ## 왜 시안의 빨강·다크를 그대로 안 그리나
 *
 * 시안의 `#FF0303`·`#21272A` 는 **디자이너의 자리표시**지 화면 사양이 아니다. 빨강을
 * 그대로 내보내면 실서비스에서 의도된 색으로 읽히고, 다크는 「그리려다 만 것」인지
 * 「원래 검은 배경 컷」인지 화면에서 구분이 안 된다. 그래서 **미완임이 보이는 한 가지
 * 모양**(점선 테두리 + 라벨)으로 통일한다.
 *
 * ## 크기는 호출처가 준다
 *
 * 자리마다 시안 규격이 다르다(정본은 `cooling-fan-assets.ts` 의 표 — 여기 사본을 두지
 * 않는다). 전역 크기를 하나 씌우면 늘어나므로 `className` 으로
 * **그 자리의 박스**를 받는다. 에셋이 채워졌을 때 `next/image` 가 예약할 종횡비는
 * 슬롯의 `width`/`height` 가 준다.
 *
 * ⚠️ **`className` 은 세 분기에 다 간다.** 한 분기에만 유효한 클래스를 여기로 넘기면
 * 다른 분기에서 조용히 사고가 난다 — 실측(2026-09-16): 플레이스홀더 라벨을 피하려던
 * `laptop:pb-6` 이 `<img>` 에도 붙어 content box 를 24px 줄였고 `object-cover` 가
 * 그만큼 잘라냈다.
 */
export const CoolingFanFigure = ({
  slot,
  mobileSlot,
  alt,
  className,
}: {
  slot: CoolingFanFigureSlot;
  /**
   * 좁은 폭에서 쓸 **다른 구도의 컷**(아트 디렉션). 같은 그림의 해상도 변형이면
   * 여기 넣지 말 것 — 그건 `next/image` 의 `srcSet` 이 알아서 한다.
   *
   * 둘 다 `src` 가 있으면 `<picture>` 로 그린다. `hidden`/`laptop:block` 두 벌
   * 렌더는 **양쪽을 다 내려받는다** — `display:none` 은 요청을 막지 않는다.
   * `<picture>` 의 `media` 는 맞는 것 하나만 받는다.
   */
  mobileSlot?: CoolingFanFigureSlot;
  /** 에셋이 있으면 `alt`, 없으면 플레이스홀더 라벨로 쓰인다. */
  alt: string;
  className?: string;
}) => {
  if (slot.src && slot.video) {
    /*
      영상 자리는 리포 공유 컴포넌트를 쓴다(화면에 들어오면 처음부터 재생 · 벗어나면 정지 ·
      `preload="none"` · 포스터). 사본(`CoolingFanVideo`)을 두었다가 리뷰 R5 1번으로 합쳤다.
      reduced-motion 처리와 접근성 이름은 그쪽에 **옵트인**으로 올려 여기서만 켠다 —
      기존 소비처 5곳의 동작은 그대로다(`viewport-video.tsx` docblock).
    */
    return (
      <ViewportVideo
        src={slot.video}
        poster={slot.src}
        loop
        respectReducedMotion
        ariaLabel={alt}
        className={className}
      />
    );
  }

  if (slot.src && mobileSlot?.src) {
    return (
      /*
        ⚠️ `next/image` 를 안 쓴다 — `<picture>` 안의 `<img>` 를 `next/image` 로 바꾸면
        그쪽이 자기 `srcSet` 을 붙여서 `<source>` 와 어느 쪽이 이기는지 예측이 안 된다.
        여기 두 컷은 **크기가 아니라 구도**가 다르므로 media 로 골라야 한다.
        브레이크포인트 1024px 는 `tailwind.config.ts:27` 의 `laptop` 과 같은 값이다 —
        여기만 어긋나면 이미지와 레이아웃이 서로 다른 폭에서 바뀐다.

        박스(크기·라운드)는 `<picture>` 가 갖고 `<img>` 는 그 안을 채우기만 한다.
        `className` 을 둘 다에 주면 `size-full` 과 호출처의 `h-60` 이 tw-merge 에서
        충돌해 `h-60` 이 이긴다 — 그러면 `<img>` 가 박스를 안 채운다.
      */
      <picture className={cn("block overflow-hidden", className)}>
        <source media="(min-width: 1024px)" srcSet={slot.src} />
        {/*
          `next/image` 를 피하면 최적화(WebP·srcSet)뿐 아니라 **lazy 도 같이 잃는다** —
          기본값이 `eager` 라 스크롤 전에 받아 온다(실측: 1440 fresh load 에서 데스크탑
          컷이 `loading=null` 로 즉시 요청). 그 둘은 속성으로 그대로 되찾을 수 있다.
        */}
        <img
          src={mobileSlot.src}
          alt={alt}
          width={mobileSlot.width}
          height={mobileSlot.height}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
      </picture>
    );
  }

  if (slot.src) {
    return (
      <Image
        src={slot.src}
        alt={alt}
        width={slot.width}
        height={slot.height}
        /*
          ⚠️ SVG 는 최적화 경로를 태우지 않는다 — `next.config` 에 `dangerouslyAllowSVG`
          가 없어서 optimizer 가 400 을 낸다(빈 이미지로만 드러난다). 리포의 기존 패턴과
          같다: `StartWithConfidence.tsx:163` 의 `unoptimized={…endsWith(".svg")}`.
        */
        unoptimized={slot.src.endsWith(".svg")}
        className={cn("object-cover", className)}
      />
    );
  }

  /*
    ⚠️ **production 에서는 빈 회색 박스만 그린다.** 점선·설명 라벨·치수(`464×302`)는 개발 중
    어느 자리가 비었는지 보려는 표시라 사용자에게 보이면 안 된다(리뷰 R2 4번 — 2026-09-29
    360·1440 에서 STEP 03 자리에 「… / 464×302」 가 렌더됐다). 이 페이지의 노출 시점은
    FE 머지가 아니라 **mgmt prod 등록**이라, 그 사이 빈 슬롯이 남아 있어도 치수가 새지 않게
    환경으로 가른다. 기준은 리포 공통 `isProd`(`NEXT_PUBLIC_ENV === "production"` —
    staging·release Vercel 이 이 값이다).
  */
  return (
    <div
      className={cn(
        "flex items-center justify-center bg-coolgray-10",
        !isProd && "border border-dashed border-coolgray-30",
        className,
      )}
      // 라벨은 미완 상태의 표시일 뿐 콘텐츠가 아니다 — 스크린리더에 읽히지 않게 둔다.
      aria-hidden
      data-figure-slot={slot.node}
    >
      {!isProd && (
        <span className="px-4 text-center text-sm text-coolgray-50">
          {alt}
          <br />
          {slot.width}&times;{slot.height}
        </span>
      )}
    </div>
  );
};

export default CoolingFanFigure;
